import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/toastMessages/toastProvider";

import {
  Typography,
  Divider,
  Stack,
  Box,
  Paper,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";

import getApiData from "../../../../hooks/apiRequests/getRequest";
import postApiData from "../../../../hooks/apiRequests/postsRequest";
import putApiData from "../../../../hooks/apiRequests/putRequest";
import deleteApiData from "../../../../hooks/apiRequests/deleteRequest";
import { clearSessionToken } from "../../../../hooks/authManagement/sessionToken";

import Userdetails from "./userdetails/userdetails";
import ResetPassword from "./userFeatures/resetPassword";
import DeleteUser from "./userFeatures/deleteUsers";
import { theme } from "../../../../configs/theme";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "15px",
  boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
  p: { xs: 2, md: 3 },
};

const ActionRow = ({ title, description, action }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "stretch", sm: "center" }}
    justifyContent="space-between"
    spacing={1.5}
    sx={{
      bgcolor: "#fff",
      borderRadius: "10px",
      border: "1px solid",
      borderColor: "divider",
      p: 1.5,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {description ? (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </Box>
    <Box sx={{ flexShrink: 0, alignSelf: { xs: "stretch", sm: "center" } }}>
      {action}
    </Box>
  </Stack>
);

const EditProfile = () => {
  const [userInfo, setUserInfo] = useState();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getApiData(`/Profile`);
        setUserInfo(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        showToast("Failed to load data. Redirecting to login...", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showToast]);

  const updateProfile = async (body) => {
    try {
      const response = await putApiData("/Update/User", body);
      if (response?.data) {
        setUserInfo(response.data);
        showToast("Profile Updated Successfully!", "success");
      } else {
        showToast("Unable to updated profile", "error");
      }
    } catch (error) {
      console.error(`Error processing updated request:`, error);
      showToast("Unable to delete profile", "error");
    }
  };

  const resetPassword = async () => {
    const body = {
      email: userInfo.email,
    };
    try {
      const response = await postApiData("/User/ResetPassword", body);
      if (response?.data) {
        showToast("User Password Reset Sent Successfully!", "success");
      } else {
        showToast("Unable to delete profile", "error");
      }
    } catch (error) {
      console.error(`Error processing Add request:`, error);
      showToast("Unable to delete profile", "error");
    }
  };

  const clearClientSession = () => {
    document.cookie =
      "token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Strict";
    localStorage.removeItem("headerData");
    clearSessionToken();
  };

  const deleteProfile = async () => {
    try {
      const response = await deleteApiData("/Delete/Profile");
      if (response?.status >= 200 && response?.status < 300) {
        showToast("Profile deleted successfully", "success");
        // API blacklists the JWT and clears the httpOnly session cookie.
        clearClientSession();
        navigate("/");
        return;
      }
      showToast("Unable to delete profile", "error");
    } catch (error) {
      console.error(`Error processing delete profile:`, error);
      showToast("Unable to delete profile", "error");
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, pb: 3 }}>
      <Stack spacing={0.5} sx={{ py: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "black" }}>
          Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account details, password reset, and profile settings.
        </Typography>
      </Stack>

      {loading ? (
        <Paper elevation={0} sx={cardSx}>
          <Box
            sx={{
              py: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <CircularProgress color="primary" size={28} />
            <Typography variant="body2" color="text.secondary">
              Loading profile...
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <Paper elevation={0} sx={{ ...cardSx, flex: 1, minWidth: 0 }}>
            <Userdetails
              theme={theme}
              userInfo={userInfo}
              editProfile={updateProfile}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              width: { xs: "100%", lg: 380 },
              flexShrink: 0,
              alignSelf: { lg: "flex-start" },
              position: { lg: "sticky" },
              top: { lg: 16 },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <ManageAccountsOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Profile actions
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Reset your password or permanently delete your account.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Stack spacing={2.5}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <LockOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Password settings
                  </Typography>
                </Stack>
                <ActionRow
                  title="Send reset email"
                  description="Email yourself a password reset link."
                  action={
                    <ResetPassword theme={theme} resetPassword={resetPassword} />
                  }
                />
              </Box>

              <Divider />

              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <DeleteOutlineIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Delete account
                  </Typography>
                </Stack>
                <ActionRow
                  title="Delete profile"
                  description="Permanently remove your account from the portal."
                  action={
                    <DeleteUser theme={theme} deleteProfile={deleteProfile} />
                  }
                />
              </Box>
            </Stack>
          </Paper>
        </Stack>
      )}
    </Box>
  );
};

export default EditProfile;
